import React, { useContext, useRef, useState } from "react";

import { useAppSetting } from "@swa-ui/application";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { window } from "@swa-ui/browser";
import { dateFormats, swaDate } from "@swa-ui/date";
import { getEnvironmentTier } from "@swa-ui/dotcom";
import { RecentSearchContext, useForm, yupResolver } from "@swa-ui/form";
import { useGeolocation } from "@swa-ui/geolocation";
import { useSearchParams } from "@swa-ui/href";
import { stationMethods } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";

import { FLIGHT_HOTEL } from "../defines/packageType";
import { getOJTFormSubmitUrl } from "../getOJTFormSubmitUrl";
import { vacationsBookingFormSchema } from "../schema";
import { useOJTWebappUrl } from "../useOJTWebappUrl";
import styles from "./useVacationsFormLogic.module.scss";

export const useVacationsFormLogic = (options = {}) => {
  const { defaultFormDataOverride = {} } = options;
  const defaultDepartureDateOffset = useAppSetting("defaultDepartureDateOffset", 14);
  const defaultReturnDateOffsetFromDepature = useAppSetting(
    "defaultReturnDateOffsetFromDeparture",
    4
  );
  const vacationsFirstDepartureDate = useAppSetting("vacationsFirstDepartureDate");
  const geolocation = useGeolocation();
  const { searchParams: queryParamsFromURL } = useSearchParams() || {};
  const { addRecentSearch } = useContext(RecentSearchContext);
  const [destinationIataCode, setDestinationIataCode] = useState("");
  const [destinationName, setDestinationName] = useState("");
  const [formData, setFormData] = useState(getDefaultFormData());
  const airDatesData = getBootstrapData("air/air-dates-data");
  const destinationSearchResultsLimit = useAppSetting("destinationSearchResultsLimit");
  const OJTBaseUrl = useOJTWebappUrl();
  const methods = useForm({
    resolver: yupResolver(vacationsBookingFormSchema({ vacationsFirstDepartureDate })),
  });
  const returnDateRef = useRef();

  const { reset, setValue, trigger } = methods;

  return {
    defaultSubmitButtonText: i18n("useVacationsFormLogic__FIND_A_VACATION"),
    formData,
    formProps: {
      departureDateProps: getDepartureDateProps(),
      destinationAirportCodeProps: getDestinationFormFieldProps(),
      formComponentProps: getFormProps(),
      originationAirportCodeProps: getOriginFormFieldProps(),
      packageOptionsProps: getPackageOptionsProps(),
      promoCodeProps: getPromoCodeProps(),
      returnDateProps: getReturnDateProps(),
      submitButtonProps: getSubmitButtonProps(),
      travelerSelectorProps: getTravelerSelectorProps(),
    },
  };

  function renderCalendarAdjunctContent() {
    const calendarDateStringFormat = i18n("useVacationsFormLogic__CALENDAR_DATE_STRING_FORMAT");

    const formattedCurrentLastDateString = swaDate(airDatesData.currentLastBookableDate).format(
      calendarDateStringFormat
    );
    const formattedFutureOpenDateString = swaDate(airDatesData.futureBookingOpenDate).format(
      calendarDateStringFormat
    );
    const formattedFutureCloseDateString = swaDate(airDatesData.futureBookingCloseDate).format(
      calendarDateStringFormat
    );

    return (
      <div className={styles.lastBookableDate}>
        {i18n("useVacationsFormLogic__LAST_BOOKABLE_DATE", { formattedCurrentLastDateString })}
        <div className={styles.bookableScheduleExtension}>
          {i18n("useVacationsFormLogic__ON_OPEN_SCHEDULE", {
            formattedFutureOpenDateString,
            formattedFutureCloseDateString,
          })}
        </div>
        <div className={styles.bookableScheduleExtension}>
          {`${i18n("useVacationsFormLogic__SUBJECT_TO_CHANGE")}`}
        </div>
      </div>
    );
  }

  function getFormProps() {
    return {
      methods,
      onSubmit: handleFormSubmit,
    };
  }

  function getPackageOptionsProps() {
    const { packageType } = formData;

    return {
      name: "packageType",
      onChange: handlePackageOptionsChange,
      value: packageType,
    };
  }

  function getTravelerSelectorProps() {
    const { packageType, travelers } = formData;

    return {
      name: "travelers",
      onChange: handleTravelerSelectorChange,
      packageType,
      travelers,
    };
  }

  function getPromoCodeProps() {
    return {
      componentProps: {
        acceptableCharacters: "1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
        value: formData.promoCode,
        maxLength: 20,
        onChange: handlePromoCodeChange,
      },
      label: i18n("useVacationsFormLogic_PromoCode__FORM_FIELD_LABEL"),
      name: "promoCode",
    };
  }

  function getOriginFormFieldProps() {
    const { originationAirportCode } = formData;

    return {
      caption: originationAirportCode
        ? stationMethods.getStationName(originationAirportCode)
        : undefined,
      componentProps: {
        defaultValue: originationAirportCode,
        invalidRouteText: i18n("useVacationsFormLogic__INVALID_ROUTE"),
        labelText: i18n("useVacationsFormLogic__FROM"),
        noMatchFoundText: i18n("useVacationsFormLogic__NO_MATCH_FOUND"),
        onChange: handleOriginationChange,
        required: true,
        softEdge: true,
      },
      label: i18n("useVacationsFormLogic__FROM"),
      name: "originationAirportCode",
    };
  }

  function getDestinationFormFieldProps() {
    const { destinationAirportCode, packageType } = formData;

    return {
      componentProps: {
        defaultValue: destinationAirportCode,
        labelText: i18n("useVacationsFormLogic__TO"),
        maxItemsToDisplay: 10,
        noMatchFoundText: i18n("useVacationsFormLogic__NO_MATCH_FOUND"),
        onBlur: handleDestinationOnBlur,
        onChange: handleDestinationChange,
        packageType,
        required: true,
        resetOnValueChange: getPackageOptionsProps().value,
        searchResultsLimit: Number(destinationSearchResultsLimit),
        softEdge: true,
      },
      label: i18n("useVacationsFormLogic__TO"),
      name: "destinationAirportCode",
    };
  }

  function getDepartureDateProps() {
    const { departureDate } = formData;

    return {
      caption: getDateCaption(departureDate),
      componentProps: getCalendarProps(i18n("useVacationsFormLogic__DEPART_DATE_ARIA"), "start"),
      label: i18n("useVacationsFormLogic__DEPART_DATE"),
      name: "departureDate",
    };
  }

  function getReturnDateProps() {
    const { returnDate } = formData;

    return {
      caption: getDateCaption(returnDate),
      componentProps: getCalendarProps(i18n("useVacationsFormLogic__RETURN_DATE_ARIA"), "end"),
      label: i18n("useVacationsFormLogic__RETURN_DATE"),
      name: "returnDate",
    };
  }

  function getCalendarProps(ariaLabel, focusField) {
    const { departureDate, returnDate } = formData;
    const firstDepartureDate = swaDate(vacationsFirstDepartureDate);

    return {
      adjunctContent: renderCalendarAdjunctContent(),
      "aria-label": ariaLabel,
      closeContent: i18n("useVacationsFormLogic__CLOSE"),
      dateEnd: returnDate,
      dateStart: departureDate,
      daysOfWeekInitials: i18n("useVacationsFormLogic__DAYS_OF_WEEK_INITIALS"),
      defaultValue: focusField === "end" ? returnDate : departureDate,
      firstBookableDate: firstDepartureDate.isAfter(swaDate())
        ? firstDepartureDate.format(dateFormats.default)
        : swaDate().add(1, "day").format(dateFormats.default),
      focusField,
      format: i18n("useVacationsFormLogic__CALENDAR_INPUT_FORMAT"),
      lastBookableDate: swaDate(airDatesData.currentLastBookableDate).format(dateFormats.default),
      numberCalendars: 2,
      numberDates: 2,
      openOnFocus: true,
      onSelectEnd: handleSelectEnd,
      onSelectStart: handleSelectStart,
      ref: focusField === "end" ? returnDateRef : null,
      required: true,
      suffixIcon: { color: "trip-selection-calendar-suffix-icon", name: "Calendar" },
      title: i18n("useVacationsFormLogic__DATE_SELECTOR_TITLE"),
    };
  }

  function getSubmitButtonProps() {
    return {
      componentProps: {
        fullWidth: true,
        styleType: "primary",
      },
      name: "submitVacationsBookingForm",
    };
  }

  function handleOriginationChange(event) {
    setFormData({ ...formData, originationAirportCode: event.target.value });
  }

  function handleDestinationOnBlur(event) {
    if (event.target.isValid) {
      setDestinationIataCode(event.target.iataCode);
      setDestinationName(event.target.name);
    } else {
      validateToField();
    }
  }

  function handleDestinationChange(event) {
    setDestinationIataCode(event.target.iataCode);
    setDestinationName(event.target.name);
  }

  function getDepartureDateOffset() {
    let offset = defaultDepartureDateOffset;

    if (vacationsFirstDepartureDate) {
      // We add + 1 to the difference because swaDate returns the difference between these dates without
      // counting today, but the calendar component does count today as the first date.
      const firstDepartureDate = swaDate(vacationsFirstDepartureDate).add(1, "day");
      const offsetWithFirstDepartureDate = firstDepartureDate.diff(swaDate(), "day");

      if (offsetWithFirstDepartureDate > offset) {
        offset = offsetWithFirstDepartureDate;
      }
    }

    return offset;
  }

  function getDefaultDepartDate() {
    return swaDate()
      .set("date", swaDate().get("date") + getDepartureDateOffset())
      .format(dateFormats.default);
  }

  function getDefaultReturnDate() {
    return swaDate()
      .set(
        "date",
        swaDate().get("date") + getDepartureDateOffset() + defaultReturnDateOffsetFromDepature
      )
      .format(dateFormats.default);
  }

  function validateToField() {
    setValue("destinationAirportCode", "");
    trigger("destinationAirportCode");
  }

  function getDateCaption(dateString) {
    return dateString
      ? swaDate(dateString).format(i18n("useVacationsFormLogic__CALENDAR_CAPTION_FORMAT"))
      : "";
  }

  function getDefaultFormData() {
    const defaultFormData = {
      ...defaultFormDataOverride,
      ...queryParamsFromURL,
    };
    const {
      departureDate,
      destinationAirportCode,
      originationAirportCode,
      packageType,
      promoCode,
      returnDate,
    } = defaultFormData;

    return {
      originationAirportCode: originationAirportCode || geolocation?.nearestStation,
      destinationAirportCode: destinationAirportCode || "",
      promoCode: promoCode || "",
      departureDate: departureDate
        ? swaDate(departureDate).format(dateFormats.default)
        : getDefaultDepartDate(),
      packageType: packageType || FLIGHT_HOTEL,
      returnDate: returnDate
        ? swaDate(returnDate).format(dateFormats.default)
        : getDefaultReturnDate(),
      travelers: [
        {
          adults: 2,
          children: 0,
          childrenAge: [],
          lapChildren: 0,
        },
      ],
    };
  }

  function handlePackageOptionsChange(packageType) {
    const defaultFormData = getDefaultFormData();

    reset(defaultFormData);
    setDestinationIataCode("");
    setDestinationName("");
    setFormData({ ...defaultFormData, packageType });
    setValue("packageType", packageType);
  }

  function handleTravelerSelectorChange(value) {
    setFormData({ ...formData, travelers: value });
    setValue("travelers", value);
  }

  function handlePromoCodeChange(event) {
    const { value: eventValue } = event.target;
    const promoCodeValue = eventValue.replace(/\s/g, "").toUpperCase();
    setFormData({ ...formData, promoCode: promoCodeValue });
    setValue("promoCode", promoCodeValue);
  }

  function handleSelectEnd(value) {
    const { departureDate } = formData;
    const newFormData = { ...formData };
    let selectionError = departureDate === value;

    if (swaDate(departureDate).isSameOrAfter(value, "day")) {
      newFormData.departureDate = value;
      newFormData.returnDate = "";
      setValue("departureDate", value);
      setValue("returnDate", "");
      selectionError = true;
    } else {
      newFormData.returnDate = value;
      setValue("returnDate", value);
    }

    setFormData(newFormData);

    return selectionError;
  }

  function handleSelectStart(value) {
    const { departureDate, returnDate } = formData;
    const newFormData = { ...formData };

    if (swaDate(value).isAfter(returnDate, "day")) {
      newFormData.returnDate = "";
      setValue("returnDate", "");
    }

    if (value !== departureDate) {
      returnDateRef?.current.focus();
    }

    setValue("departureDate", value);
    newFormData.departureDate = value;
    setFormData(newFormData);
  }

  function handleFormSubmit(submittedFormData) {
    const { searchParams = {} } = defaultFormDataOverride;
    const encodedURL = getOJTFormSubmitUrl(OJTBaseUrl, submittedFormData, searchParams);

    if (destinationName) {
      updateRecentSearches(submittedFormData, encodedURL);
      window.open(encodedURL, "_blank");
    } else {
      validateToField();
    }
  }

  function updateRecentSearches(submittedFormData, encodedURL) {
    const { departureDate, originationAirportCode, packageType, returnDate, travelers } =
      submittedFormData;
    let adults = 0;
    let children = 0;

    travelers.forEach((room) => {
      adults += room.adults;
      children += room.children + room.lapChildren;
    });

    addRecentSearch(
      {
        adults,
        children,
        departureDate,
        desktopImage: `/swa-resources/images/swav/my_recent_searches/${destinationIataCode.toUpperCase()}/desktop.png`,
        destinationAirportCode: destinationName,
        mobileImage: `/swa-resources/images/swav/my_recent_searches/${destinationIataCode.toUpperCase()}/mobile.png`,
        originationAirportCode,
        packageType,
        returnDate,
        rooms: travelers.length,
        searchVacationRedirectPageUrl: encodedURL,
      },
      packageType
    );
  }
};
